import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { answeredAntibiogram, cultureShape, cultureSummary, type OrderItemResults, printedDecimals, type ReportArtifact, type WorklistItem } from "@probetalab/contracts";
import { get, getBlob, run } from "../lib/api.js";
import { fmtDateTime, fmtNumber, fmtTime, plural } from "../lib/format.js";
import { useBranch, useSession } from "../lib/session.js";
import { COLOUR_LABEL, ITEM_STATE_LABEL, itemTone } from "../lib/status.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useQuery } from "../lib/useQuery.js";
import { Alert, Button, Card, Chip, Empty, Field, Input, Loading, PageHead, Sheet, Switch, Textarea } from "../components/ui.js";
import { DateRangeFilter, dateRangeQuery, describeDateRange, useDateRange } from "../components/DateRangeFilter.js";
import { Icon } from "../components/icons.js";
import { RichTextView } from "../components/RichText.js";
import { RefText } from "../components/RefText.js";
import { StudyAttachments } from "../components/CaptureAttachments.js";
import { ATTACH_KEY, LAB_KEY } from "../components/CaptureRows.js";

/**
 * Validates studies exactly as the Validación screen does, from wherever the chemist is: the value
 * hashes the chemist saw (refused if the stored values changed since), then the order's report
 * artifact, final when every live study is validated and partial otherwise. Validating a
 * *declared* correction (a study in `amended` whose result had already left the laboratory) issues
 * an `amendment` artifact instead: the report that replaces the one already issued, which is kept
 * (ADR 0010, ADR 0022). An undeclared correction (ADR 0040) never issued anything, so its sheet is
 * an ordinary final or partial.
 */
export async function validateAndRender(
  orderId: string,
  items: readonly (Pick<OrderItemResults, "order_item_id" | "value_hash"> & {
    state?: OrderItemResults["state"];
    correction_declared?: OrderItemResults["correction_declared"];
  })[],
  branchId: string,
): Promise<{ allValidated: boolean; artifact: ReportArtifact }> {
  const hashes: Record<string, string> = {};
  for (const it of items) if (it.value_hash) hashes[it.order_item_id] = it.value_hash;
  await run("results.validate", { order_item_ids: items.map((i) => i.order_item_id), value_hashes: hashes }, { branch_id: branchId });
  const detail = await get("order", { params: { id: orderId } });
  const allValidated = detail.items.filter((i) => i.state !== "cancelled").every((i) => i.state === "validated" || i.state === "issued");
  // ADR 0040: only a declared correction is issued as «Corrección»; `correction_declared` defaults
  // true, so a caller that does not know (an older response) keeps ADR 0022's behaviour.
  const correction = items.some((i) => i.state === "amended" && (i.correction_declared ?? true));
  const kind = correction ? "amendment" : allValidated ? "final" : "partial";
  const artifact = await run<"report.render", ReportArtifact>("report.render", { order_id: orderId, kind, order_item_ids: [], options: { letterhead: true, signature: true } }, { branch_id: branchId });
  return { allValidated, artifact };
}

export function validatedMessage(count: number, allValidated: boolean, artifact: ReportArtifact): string {
  const report = artifact.kind === "amendment" ? "Informe corregido" : `Informe ${allValidated ? "final" : "parcial"}`;
  return `${plural(count, "estudio validado", "estudios validados")}. ${report} listo (${artifact.filename}).`;
}

/**
 * Validación: orders with captured studies, grouped. The chemist opens one, reviews, sees the draft,
 * signs. Today's work by default (Very, 2026-11-02 19:25: the migrated Toronja backlog buried it);
 * older days one tap away, and the migrated history only on request.
 */
export function ValidationPage() {
  const branch = useBranch();
  const navigate = useNavigate();
  const [range, setRange] = useDateRange();
  const [includeMigrated, setIncludeMigrated] = useState(false);
  const dates = dateRangeQuery(range);
  const migrated = range.preset === "any" && includeMigrated;
  // Captured studies, and corrections saved but not yet validated again (`amended`, ADR 0022).
  const list = useQuery(async () => {
    const base = { branch_id: branch.id, ...dates, ...(migrated ? {} : { origin: "app" as const }), limit: 200 };
    const [amended, captured] = await Promise.all([get("worklist", { query: { ...base, state: "amended" } }), get("worklist", { query: { ...base, state: "captured" } })]);
    const seen = new Set<string>();
    return { items: [...captured.items, ...amended.items].filter((i) => !seen.has(i.order_item_id) && !!seen.add(i.order_item_id)) };
  }, [branch.id, dates.day, dates.from, dates.to, migrated]);
  const groups = useMemo(() => {
    const m = new Map<string, WorklistItem[]>();
    for (const it of list.data?.items ?? []) m.set(it.order_id, [...(m.get(it.order_id) ?? []), it]);
    return [...m.values()];
  }, [list.data]);
  return (
    <>
      <PageHead kicker={branch.name} title="Validación" lede="Estudios capturados que esperan la firma del químico. Nada se entrega ni se ve fuera del laboratorio hasta validarlo." />
      <div className="toolbar">
        <DateRangeFilter value={range} onChange={setRange} allowAny />
        {range.preset === "any" && (
          <Switch checked={includeMigrated} onChange={setIncludeMigrated}>
            Incluir historial migrado
          </Switch>
        )}
      </div>
      {list.loading && !list.data ? (
        <Loading />
      ) : groups.length === 0 ? (
        <Card>
          <Empty
            icon="badge"
            title={range.preset === "any" ? "Nada por validar" : `Nada por validar ${describeDateRange(range)}`}
            hint={range.preset === "any" ? "Los estudios capturados aparecerán aquí." : "Elige otras fechas o toca «Todos los días» para ver los estudios de días anteriores."}
          />
        </Card>
      ) : (
        <Card pad={false}>
          {groups.map((items) => {
            const f = items[0]!;
            return (
              <button key={f.order_id} type="button" className="list-row clickable" onClick={() => navigate(`/validacion/${f.order_id}`)} style={{ minHeight: 72 }}>
                <span className="folio folio-col">
                  {f.order_folio}
                </span>
                <span className="grow">
                  <div className="primary">{f.patient_name}</div>
                  <div className="secondary">
                    {items.map((i) => i.study_clave).join(", ")} · registrado {dates.day === undefined ? fmtDateTime(f.registered_at) : fmtTime(f.registered_at)}
                  </div>
                </span>
                {f.origin === "migrated" && <Chip>Migrado</Chip>}
                {items.some((i) => i.state === "amended") && <Chip tone="violet">Corrección</Chip>}
                {items.some((i) => i.sample_pending) && <Chip tone="red">Muestra pendiente</Chip>}
                {f.balance_cents > 0 && <Chip tone="amber">Adeudo</Chip>}
                <Chip tone="amber" large>
                  {plural(items.length, "estudio", "estudios")}
                </Chip>
                <Icon name="chevronRight" style={{ color: "var(--ink-3)" }} />
              </button>
            );
          })}
        </Card>
      )}
    </>
  );
}

export function ValidationOrderPage() {
  const { orderId = "" } = useParams();
  const branch = useBranch();
  const { me } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const order = useQuery(() => get("order", { params: { id: orderId } }), [orderId]);
  const [results, setResults] = useState<Record<string, OrderItemResults>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [returning, setReturning] = useState<OrderItemResults | null>(null);

  const items = useMemo(() => (order.data?.items ?? []).filter((i) => i.state !== "cancelled"), [order.data]);
  const captured = items.filter((i) => i.state === "captured" || i.state === "amended");

  useEffect(() => {
    let alive = true;
    (async () => {
      const entries = await Promise.all(items.filter((i) => i.state !== "pending").map(async (i) => [i.id, await get("orderItemResults", { params: { id: i.id } })] as const));
      if (alive) setResults(Object.fromEntries(entries));
    })().catch((e) => toast.error(errorMessage(e)));
    return () => {
      alive = false;
    };
  }, [items, toast]);

  useEffect(() => {
    setSelected(new Set(captured.map((i) => i.id)));
  }, [order.data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let url: string | null = null;
    if (!orderId || items.length === 0) return;
    getBlob("orderPreviewPdf", { params: { id: orderId }, query: { letterhead: true, signature: true, staff_view: true } })
      .then((b) => {
        url = URL.createObjectURL(b);
        setPdfUrl(url);
      })
      .catch(() => setPdfUrl(null));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [orderId, items.length, order.data?.state]);

  if (order.loading && !order.data) return <Loading />;
  if (!order.data) return <Empty icon="warning" title="No se encontró la solicitud" hint={order.error ?? undefined} />;
  const o = order.data;
  const canSign = !!me?.user.can_validate;

  async function validate() {
    setBusy(true);
    try {
      const ids = [...selected];
      const { allValidated, artifact } = await validateAndRender(orderId, ids.map((id) => ({ order_item_id: id, value_hash: results[id]?.value_hash ?? null, state: results[id]?.state, correction_declared: results[id]?.correction_declared })), branch.id);
      toast.success(validatedMessage(ids.length, allValidated, artifact));
      navigate(`/entrega/${orderId}`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead
        kicker={
          <>
            <Link to={`/solicitudes/${o.id}`} style={{ color: "inherit" }}>
              {o.folio}
            </Link>{" "}
            · {o.patient.age_text} · {o.doctor_display}
          </>
        }
        title={`${o.patient.first_name} ${o.patient.paternal_surname} ${o.patient.maternal_surname ?? ""}`.trim()}
        lede={o.condition ? `Condición: ${o.condition}` : undefined}
        actions={
          <>
            <Button variant="ghost" icon="chevronLeft" onClick={() => navigate("/validacion")}>
              Volver
            </Button>
            <Button variant="primary" size="lg" icon="badge" busy={busy} disabled={!canSign || selected.size === 0} onClick={() => void validate()}>
              Validar {selected.size > 0 ? `${selected.size} ${selected.size === 1 ? "estudio" : "estudios"}` : ""}
            </Button>
          </>
        }
      />
      {!canSign && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="amber">Tu usuario no tiene permiso para validar. Un químico con cédula registrada debe firmar los resultados.</Alert>
        </div>
      )}
      {canSign && me && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="blue" icon="badge">
            Firmará <strong>{me.user.full_name}</strong>
            {me.user.cedula ? <> · Cédula profesional <strong>{me.user.cedula}</strong></> : null}. El nombre y la cédula quedan impresos en el informe.
          </Alert>
        </div>
      )}
      {o.has_pending_samples && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="red">Hay una muestra pendiente en esta solicitud. Puedes validar lo capturado; el informe será parcial y no se entrega hasta completar.</Alert>
        </div>
      )}
      <div className="split">
        <div className="stack-lg">
          {items.map((it) => {
            const r = results[it.id];
            const isCaptured = it.state === "captured" || it.state === "amended";
            return (
              <Card
                key={it.id}
                title={
                  <label className="row" style={{ gap: 12, cursor: isCaptured ? "pointer" : "default" }}>
                    {isCaptured && (
                      <input
                        type="checkbox"
                        style={{ width: 22, height: 22, accentColor: "var(--brand)" }}
                        checked={selected.has(it.id)}
                        onChange={(e) =>
                          setSelected((s) => {
                            const n = new Set(s);
                            if (e.target.checked) n.add(it.id);
                            else n.delete(it.id);
                            return n;
                          })
                        }
                      />
                    )}
                    <span className="mono muted">{it.study_clave}</span>
                    <span className="h3">{it.study_name}</span>
                    <Chip tone={itemTone(it.state)}>{ITEM_STATE_LABEL[it.state]}</Chip>
                  </label>
                }
                actions={
                  isCaptured && r ? (
                    <div className="row">
                      <Button size="sm" variant="critical" icon="pen" onClick={() => navigate(`/captura/${it.id}`)}>
                        Corregir
                      </Button>
                      <Button size="sm" variant="ghost" icon="refresh" onClick={() => setReturning(r)}>
                        Regresar a captura
                      </Button>
                    </div>
                  ) : undefined
                }
                pad={false}
              >
                {it.state === "pending" ? (
                  <div className="list-row faint">Sin capturar todavía.</div>
                ) : !r ? (
                  <Loading />
                ) : (
                  <div className="table-scroll">
                    <table className="table compact">
                      <tbody>
                        {r.analytes.map((a) => {
                          // A culture shows the antibiotics chosen for this order only, none when negative (ADR 0019).
                          const culture = cultureShape(r.analytes);
                          const cultureDoc = culture ? r.values.find((x) => x.analyte_id === culture.document.analyte_id) : undefined;
                          if (culture?.antibiotics.some((x) => x.analyte_id === a.analyte_id) && (cultureDoc?.culture?.outcome === "negative" || !r.values.some((x) => x.analyte_id === a.analyte_id))) return null;
                          if (a.kind === "document") {
                            const dv = r.values.find((x) => x.analyte_id === a.analyte_id);
                            return (
                              <tr key={a.analyte_id}>
                                <td style={{ width: "36%", verticalAlign: "top" }}>{a.name}</td>
                                <td colSpan={3} className="validation-doc">
                                  {dv?.culture && <div style={{ fontWeight: 600, marginBottom: 6 }}>{cultureSummary(dv.culture)}</div>}
                                  {dv?.value_text ? <RichTextView html={dv.value_text} /> : !dv?.culture && <span className="faint">—</span>}
                                  {/* ADR 0026: each classified organism's own antibiogram, as it prints (answered only). */}
                                  {dv?.culture?.outcome === "positive" &&
                                    dv.culture.isolates.map((iso, i) => {
                                      const abx = answeredAntibiogram(iso);
                                      if (!abx.length) return null;
                                      return (
                                        <div key={i} className="validation-abx">
                                          <div className="validation-abx-title">Antibiograma{dv.culture!.isolates.length > 1 ? ` · ${iso.organism}` : ""}</div>
                                          <ul aria-label={`Antibiograma de ${iso.organism}`}>
                                            {abx.map((e) => (
                                              <li key={e.antibiotic}>
                                                <span>{e.antibiotic}</span>
                                                <strong>{e.result}</strong>
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      );
                                    })}
                                </td>
                              </tr>
                            );
                          }
                          if (a.kind === "subtitle") {
                            return (
                              <tr key={a.analyte_id} style={{ background: "var(--paper-2)" }}>
                                <td colSpan={4} style={{ fontWeight: 600, color: "var(--ink-2)" }}>
                                  {a.name}
                                </td>
                              </tr>
                            );
                          }
                          const v = r.values.find((x) => x.analyte_id === a.analyte_id);
                          const prev = r.previous.find((p) => p.analyte_id === a.analyte_id);
                          return (
                            <tr key={a.analyte_id} className={v ? `res-tint ${v.colour === "green" ? "" : v.colour}` : ""}>
                              <td style={{ width: "36%" }}>{a.name}</td>
                              <td className="right tnum" style={{ fontWeight: 600, fontSize: "1.0625rem" }}>
                                {v ? (
                                  <span className="res" style={{ justifyContent: "flex-end" }}>
                                    <span className={`res-dot ${v.colour}`} title={COLOUR_LABEL[v.colour]} />
                                    {/* Same decimals as the printed sheet: never a number its own flag contradicts (ADR 0039). */}
                                    {v.value_numeric !== null ? fmtNumber(v.value_numeric, printedDecimals(v.value_numeric, a.decimals, v.flag, { low: a.low, high: a.high })) : v.value_text}
                                    {v.flag && v.flag !== "N" && <span className={`flag ${v.flag}`}>*{v.flag === "L" ? "B" : "A"}</span>}
                                  </span>
                                ) : (
                                  <span className="faint">—</span>
                                )}
                              </td>
                              <td className="muted">{a.unit ?? ""}</td>
                              <td className="muted small">
                                <RefText text={a.interval_text ?? a.expected_text} />
                                {prev && (
                                  <span className="faint" style={{ display: "block" }}>
                                    {" "}
                                    · antes {prev.value_numeric !== null ? fmtNumber(prev.value_numeric, a.decimals) : prev.value_text}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {r.note && (
                      <div className="study-note" style={{ background: "transparent" }}>
                        <span className="muted small">Nota</span>
                        <RichTextView html={r.note} />
                      </div>
                    )}
                    {/* ADR 0030: the attached documents are part of what is validated; «Ver» opens each. */}
                    {((r.attachments?.length ?? 0) > 0 || r.performed_by_lab) && (
                      <StudyAttachments
                        r={r}
                        draft={{ [ATTACH_KEY]: (r.attachments ?? []).map((a) => a.id).join(","), [LAB_KEY]: r.performed_by_lab ?? "" }}
                        locked
                        collapsible={false}
                        onChange={() => undefined}
                      />
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
        <div className="sticky">
          <Card title="Vista previa del informe" actions={<span className="chip amber">Resultados no validados</span>} pad={false}>
            {pdfUrl ? <iframe title="Vista previa" src={`${pdfUrl}#toolbar=0&view=FitH`} className="pdf-frame" /> : <Loading label="Generando vista previa…" />}
          </Card>
        </div>
      </div>
      <ReturnSheet item={returning} onClose={() => setReturning(null)} onDone={() => void order.reload()} />
    </>
  );
}

/** "Regresar a captura": `results.return` with the reason the capturist reads. Shared with the capture screens. */
export function ReturnSheet({ item, onClose, onDone }: { item: OrderItemResults | null; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Sheet
      open={item !== null}
      onClose={onClose}
      title={item ? `Regresar ${item.study_clave} a captura` : ""}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="amber"
            icon="refresh"
            busy={busy}
            disabled={reason.trim().length < 3}
            onClick={async () => {
              if (!item) return;
              setBusy(true);
              try {
                await run("results.return", { order_item_id: item.order_item_id, reason: reason.trim() });
                toast.success("Regresado a captura.");
                setReason("");
                onDone();
                onClose();
              } catch (e) {
                toast.error(errorMessage(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            Regresar
          </Button>
        </>
      }
    >
      <Field label="Motivo">
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Qué debe revisarse" autoFocus />
      </Field>
      <Input type="hidden" />
    </Sheet>
  );
}
